import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import AVIcon from "../icons/AllVoicedIcons";

export const CONTEXTUAL_HELP_VIEWPORT_MARGIN = 16;
export const CONTEXTUAL_HELP_VERTICAL_OFFSET = 10;
export const CONTEXTUAL_HELP_PREFERRED_WIDTH = 320;

export function getContextualHelpWidth({
  viewportWidth,
  margin = CONTEXTUAL_HELP_VIEWPORT_MARGIN,
  preferredWidth = CONTEXTUAL_HELP_PREFERRED_WIDTH,
}) {
  const safeViewportWidth = Math.max(viewportWidth, margin * 2);

  return Math.max(
    0,
    Math.min(preferredWidth, safeViewportWidth - margin * 2)
  );
}

export function computeContextualHelpPosition({
  triggerRect,
  panelRect,
  viewportWidth,
  viewportHeight,
  margin = CONTEXTUAL_HELP_VIEWPORT_MARGIN,
  offset = CONTEXTUAL_HELP_VERTICAL_OFFSET,
}) {
  const safeViewportWidth = Math.max(viewportWidth, margin * 2);
  const safeViewportHeight = Math.max(viewportHeight, margin * 2);
  const maxWidth = Math.max(0, safeViewportWidth - margin * 2);
  const maxHeight = Math.max(0, safeViewportHeight - margin * 2);
  const panelWidth = Math.min(panelRect.width || 0, maxWidth);
  const panelHeight = Math.min(panelRect.height || 0, maxHeight);

  let left = (triggerRect.right || 0) - panelWidth;
  left = Math.max(
    margin,
    Math.min(left, safeViewportWidth - panelWidth - margin)
  );

  const belowTop = (triggerRect.bottom || 0) + offset;
  const aboveTop = (triggerRect.top || 0) - offset - panelHeight;
  const fitsBelow = belowTop + panelHeight <= safeViewportHeight - margin;
  const fitsAbove = aboveTop >= margin;

  let top = belowTop;

  if (!fitsBelow && fitsAbove) {
    top = aboveTop;
  } else {
    top = Math.max(
      margin,
      Math.min(top, safeViewportHeight - panelHeight - margin)
    );
  }

  return {
    left,
    top,
    maxWidth,
    maxHeight,
  };
}

export default function ApplicationContextualHelp({
  label,
  title,
  children,
  learnMoreLabel,
  learnMoreTo,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();
  const titleId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current || !panelRef.current) {
        return;
      }

      const triggerRect =
        buttonRef.current.getBoundingClientRect();
      const panelRect =
        panelRef.current.getBoundingClientRect();

      const nextPosition = computeContextualHelpPosition({
        triggerRect,
        panelRect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      const width = getContextualHelpWidth({
        viewportWidth: window.innerWidth,
      });

      setPanelStyle({
        left: `${nextPosition.left}px`,
        top: `${nextPosition.top}px`,
        width: `${width}px`,
        maxWidth: `${nextPosition.maxWidth}px`,
        maxHeight: `${nextPosition.maxHeight}px`,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <span
      className={[
        "av-app-contextual-help",
        className,
      ].filter(Boolean).join(" ")}
      ref={containerRef}
    >
      <button
        aria-controls={panelId}
        aria-expanded={open ? "true" : "false"}
        aria-haspopup="dialog"
        aria-label={label}
        className="av-app-contextual-help__trigger"
        onClick={() => setOpen((value) => !value)}
        ref={buttonRef}
        type="button"
      >
        <AVIcon
          aria-hidden="true"
          icon="info"
          size={16}
        />
      </button>

      {open ? (
        <div
          aria-labelledby={titleId}
          className="av-app-contextual-help__panel"
          id={panelId}
          ref={panelRef}
          role="dialog"
          style={panelStyle || undefined}
        >
          <h3
            className="av-app-contextual-help__title"
            id={titleId}
          >
            {title}
          </h3>
          <div className="av-app-contextual-help__body">
            {children}
          </div>

          {learnMoreLabel && learnMoreTo ? (
            <Link
              className="av-app-contextual-help__link"
              to={learnMoreTo}
            >
              {learnMoreLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}
